// Step-3 evidence uploader (screens-buyer-app §6, D4).
//
// Reuses the EXACT supplier upload mechanism (MediaApi.upload → POST
// /mdi/media/file/order/upload, multipart field "file", returns { url }) — the
// same flow as src/pages/return/warehouse-receipt/PhotoUploader.tsx. Adds the
// buyer-spec constraints: jpeg/png/heic/webp only, max 5MB each, max 10 total,
// mobile camera capture. The resulting URL strings feed ICreateReturnBody.evidencePhotos.
import { ActionIcon, Box, Button, FileButton, Group, Image, LoadingOverlay, Stack, Text } from "@mantine/core";
import { CameraIcon, TrashIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import classes from "./wizard-stepper.module.css";
import { MediaApi } from "../../../../apis";
import HttpHandler from "../../../../utils/http-handler";
import { message } from "../../../../utils/message";

const MAX_PHOTOS = 10;
const MAX_BYTES = 5 * 1024 * 1024; // 5MB
const ACCEPT = "image/jpeg,image/png,image/heic,image/webp";
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/heic", "image/webp"];

interface Props {
  value: string[];
  onChange: (urls: string[]) => void;
  max?: number;
  error?: string | null;
  disabled?: boolean;
}

export function WizardPhotoUploader({ value, onChange, max = MAX_PHOTOS, error, disabled }: Props) {
  const { t } = useTranslation("buyerApp");
  const [loading, setLoading] = useState(false);
  const atLimit = value.length >= max;

  const onPick = (blob: File | null) => {
    if (!blob) return;
    // Client-side guards (the message util shows the toast; no double-toast since
    // these never reach http-handler).
    const typeOk = ACCEPTED_TYPES.includes(blob.type) || /\.(jpe?g|png|heic|webp)$/i.test(blob.name);
    if (!typeOk) {
      message.error(t("photoUploader.onlyImageTypes"));
      return;
    }
    if (blob.size > MAX_BYTES) {
      message.error(t("photoUploader.maxSize"));
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const form = new FormData();
        form.append("file", blob);
        const res = await MediaApi.upload(form);
        const url = (res as { url?: string })?.url;
        if (url) onChange([...value, url]);
      } catch (err) {
        message.error((err as HttpHandler)?.message ?? t("photoUploader.uploadError"));
      } finally {
        setLoading(false);
      }
    })();
  };

  const removeAt = (index: number) => onChange(value.filter((_, i) => i !== index));

  return (
    <Stack gap="xs">
      <Box pos="relative">
        <LoadingOverlay visible={loading} loaderProps={{ size: "sm" }} />
        <Group gap="sm" align="center">
          {value.length === 0 ? (
            <Box className={classes.photoEmpty} aria-hidden>
              <CameraIcon size={26} />
            </Box>
          ) : null}
          {value.map((url, i) => (
            <Box key={`${url}-${i}`} pos="relative" w={80} h={80}>
              <Image src={url} alt={t("photoUploader.imageAlt", { n: i + 1 })} w={80} h={80} radius="md" fit="cover" />
              {!disabled ? (
                <ActionIcon
                  color="dark"
                  variant="filled"
                  size="sm"
                  radius="xl"
                  pos="absolute"
                  top={-6}
                  right={-6}
                  onClick={() => removeAt(i)}
                  aria-label={t("photoUploader.removeImage")}
                >
                  <TrashIcon size={14} />
                </ActionIcon>
              ) : null}
            </Box>
          ))}
          {!atLimit && !disabled ? (
            <FileButton onChange={onPick} accept={ACCEPT} capture="environment">
              {(props) => (
                <Button {...props} variant="default" leftSection={<CameraIcon size={18} />}>
                  {t("photoUploader.add")}
                </Button>
              )}
            </FileButton>
          ) : null}
        </Group>
      </Box>
      {error ? (
        <Text size="sm" c="red">{error}</Text>
      ) : (
        <Text className={classes.subtitle}>
          {t("photoUploader.hint", { current: value.length, max })}
        </Text>
      )}
    </Stack>
  );
}
